"use client";

import {
  getThemeColorForListingStatus,
  type ListingStatusColor,
} from "@/lib/listing-utils";
import type { AnyDetailedListing } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

export type ListingStatusBadgeProps = {
  listing?: Pick<AnyDetailedListing, "status">;
  status?: AnyDetailedListing["status"];
  small?: boolean;
  className?: string;
};

function getBadgeStyles(color: ListingStatusColor) {
  switch (color) {
    case "green":
      return {
        bg: "bg-theme-green-soft",
        text: "text-foreground",
        dot: "bg-theme-emerald",
      };
    case "orange":
      return {
        bg: "bg-theme-orange-soft",
        text: "text-foreground",
        dot: "bg-theme-orange",
      };
    case "red":
      return {
        bg: "bg-theme-red-soft",
        text: "text-foreground",
        dot: "bg-theme-red",
      };
    case "blue":
      return {
        bg: "bg-theme-blue-soft",
        text: "text-foreground",
        dot: "bg-theme-blue",
      };
    case "purple":
      return {
        bg: "bg-theme-purple/15",
        text: "text-foreground",
        dot: "bg-theme-purple",
      };
    default:
      return {
        bg: "bg-muted",
        text: "text-muted-foreground",
        dot: "bg-muted-foreground",
      };
  }
}

export function ListingStatusBadge({
  listing,
  status,
  small,
  className,
}: ListingStatusBadgeProps) {
  const t = useTranslations();

  const finalStatus = listing?.status ?? status;

  const themeColor = useMemo<ListingStatusColor>(() => {
    if (!finalStatus) {
      return "black";
    }
    return getThemeColorForListingStatus(finalStatus);
  }, [finalStatus]);

  const styles = getBadgeStyles(themeColor);
  const isSold = finalStatus === "SOLD";

  if (!finalStatus) {
    return null;
  }

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full font-medium",
        styles.bg,
        styles.text,
        {
          "px-2 py-0.5 text-xs": small,
          "px-3 py-1 text-[13px]": !small,
        },
        className,
      )}
    >
      <span
        className={cn("shrink-0 rounded-full", styles.dot, {
          "size-1.5": small,
          "size-2": !small,
        })}
      />

      <span className={cn("shrink-0", { "line-through": isSold })}>
        {t(`saas.enums.listingStatus.${finalStatus}`)}
      </span>
    </div>
  );
}

ListingStatusBadge.displayName = "ListingStatusBadge";
